<template>
  <base-drawer
    :show="show"
    :title="drawerTitle"
    @close="onClose"
  >
    <!-- Back button shown when in a sub-view (matches BookingDrawer's
         drawer-views back-arrow pattern). -->
    <template v-if="currentView !== 'main'" #title-prefix>
      <button class="csd-back-btn" @click="exitPricingView">
        <i class="tim-icons icon-minimal-left"></i>
      </button>
    </template>

    <div class="create-slot-form">
    <!-- Loading skeleton -->
    <template v-if="!ready">
      <div class="skeleton-section mb-3">
        <div class="skeleton-row" style="width: 40%; height: 14px; margin-bottom: 16px;"></div>
        <div class="skeleton-form-row">
          <div class="skeleton-row" style="width: 100%; height: 36px;"></div>
          <div class="skeleton-row" style="width: 100%; height: 36px;"></div>
        </div>
      </div>
      <div class="skeleton-section mb-3">
        <div class="skeleton-row" style="width: 30%; height: 14px; margin-bottom: 16px;"></div>
        <div class="skeleton-form-row">
          <div class="skeleton-row" style="width: 100%; height: 36px;"></div>
          <div class="skeleton-row" style="width: 100%; height: 36px;"></div>
        </div>
        <div class="skeleton-form-row mt-2">
          <div class="skeleton-row" style="width: 100%; height: 36px;"></div>
          <div class="skeleton-row" style="width: 100%; height: 36px;"></div>
        </div>
      </div>
    </template>

    <template v-else-if="currentView === 'main'">
      <!-- Section 1: Slot details (event/status + timing — matches
           BookingDrawer's slot-details section which shows date/time inline) -->
      <drawer-section title="Slot details" icon="icon-calendar-60">
        <div class="form-row">
          <div class="form-col">
            <base-input label="Event" :error="errs.game">
              <el-select
                v-model="selectedGame"
                class="select-primary"
                placeholder="Select event"
                value-key="id"
                filterable
              >
                <el-option
                  v-for="game in games"
                  class="select-primary"
                  :label="game.name"
                  :value="game"
                  :key="'G' + game.id"
                />
              </el-select>
            </base-input>
          </div>
          <div class="form-col">
            <base-input label="Status">
              <el-select
                v-model="selectedStatus"
                class="select-primary"
                placeholder="Status"
              >
                <el-option
                  v-for="(s, i) in allStatuses"
                  class="select-primary"
                  :label="s.label"
                  :value="s.value"
                  :key="'S' + i"
                />
              </el-select>
            </base-input>
          </div>
        </div>

        <!-- All day toggle -->
        <div class="toggle-row mb-3">
          <el-switch
            active-color="#e14eca"
            v-model="slot.is_all_day"
            @change="handleAllDayToggle"
          />
          <label
            class="mb-0 ml-2"
            style="cursor: pointer"
            @click="slot.is_all_day = !slot.is_all_day; handleAllDayToggle()"
          >
            All day event
          </label>
        </div>

            <!-- All day text -->
            <div v-if="slot.is_all_day" class="mb-3">
              <info-hint variant="primary">
                This text will be displayed on the booking slot instead of the time range for all-day events.
              </info-hint>
              <label class="text-white">Display text</label>
              <base-input v-model="slot.all_day_text" placeholder="e.g., Available all day"></base-input>
            </div>

            <!-- Date/time (when not all day) -->
            <template v-if="!slot.is_all_day">
              <div class="form-row">
                <div class="form-col">
                  <base-input label="Start date" :error="errs.start_date">
                    <el-date-picker
                      :editable="false"
                      type="date"
                      placeholder="Select date"
                      :format="dayOfWeek + dateFormatCal"
                      v-model="slot.start_date"
                      :picker-options="{ firstDayOfWeek: firstDayOfWeek }"
                    />
                  </base-input>
                </div>
                <div class="form-col">
                  <base-input label="Start time" :error="errs.start_time">
                    <el-time-picker
                      v-model="slot.start_time"
                      :format="pickerTimeFormat"
                      placeholder="Select time"
                    />
                  </base-input>
                </div>
              </div>
              <div class="form-row">
                <div class="form-col">
                  <base-input label="End date" :error="errs.end_date">
                    <el-date-picker
                      :editable="false"
                      type="date"
                      placeholder="Select date"
                      :format="dayOfWeek + dateFormatCal"
                      v-model="slot.end_date"
                      :picker-options="{ firstDayOfWeek: firstDayOfWeek }"
                    />
                  </base-input>
                </div>
                <div class="form-col">
                  <base-input label="End time" :error="errs.end_time">
                    <el-time-picker
                      v-model="slot.end_time"
                      :format="pickerTimeFormat"
                      placeholder="Select time"
                    />
                  </base-input>
                </div>
              </div>
            </template>
      </drawer-section>

      <!-- Section 3: Booking options (available/call_to_book only) -->
      <transition name="section-fade">
        <drawer-section
          v-if="selectedStatus === 'available' || selectedStatus === 'call_to_book' || selectedStatus === 'requestable'"
          key="booking-options"
          title="Booking options"
          icon="icon-settings"
        >

              <div class="toggle-row mb-3">
                <el-switch active-color="#e14eca" v-model="allow_late_booking" />
                <label class="mb-0 ml-2" style="cursor: pointer" @click="allow_late_booking = !allow_late_booking">
                  Allow bookings after start time
                </label>
              </div>

              <!-- Minimum booking notice / max time after start -->
              <info-hint v-if="!allow_late_booking" variant="primary">
                Choose how soon before the event start time a customer can book. This setting will help prevent last minute bookings.
              </info-hint>
              <info-hint v-else variant="primary">
                Allow customers to book up to this amount of time after the event has already started. Useful for events that allow late entry.
              </info-hint>
              <value-period-input
                class="mb-4"
                v-model.number="min_booking_value"
                :period.sync="min_booking_period"
                :periods="min_periods"
                :label="allow_late_booking ? 'Maximum time after booking start' : 'Minimum booking notice'"
                :min="0"
              />

              <info-hint v-if="!allow_late_booking" variant="primary">
                Choose what customers see once this slot enters the minimum notice window: a call to book label, a request to book button, or nothing.
              </info-hint>
              <div class="mb-4" v-if="!allow_late_booking">
                <label>When minimum notice passes</label>
                <base-input class="mb-0">
                  <el-select v-model="pastNoticeBehavior" class="select-primary">
                    <el-option class="select-primary" label="Show as call to book" :value="0"></el-option>
                    <el-option class="select-primary" label="Show as request to book" :value="1"></el-option>
                    <el-option class="select-primary" label="Hide slot" :value="2"></el-option>
                  </el-select>
                </base-input>
              </div>

              <!-- Maximum bookings in advance -->
              <info-hint variant="primary">
                Choose how far in advance customers can book this event. By default, bookings are generated 12 months in advance.
              </info-hint>
              <value-period-input
                class="mb-2"
                v-model.number="max_booking_value"
                :period.sync="max_booking_period"
                :periods="max_periods"
                label="Maximum bookings in advance"
                :min="0"
              />

        </drawer-section>
      </transition>

      <!-- Section 4: Display options (available/call_to_book only) -->
      <transition name="section-fade">
        <drawer-section
          v-if="selectedStatus === 'available' || selectedStatus === 'call_to_book'"
          key="display-options"
          title="Display options"
          icon="icon-image-02"
        >
              <info-hint v-if="selectedStatus === 'available' && !allow_late_booking" variant="primary">
                This text will be visible to customers when a slot is blocked off due to minimum booking notice coming into effect.
              </info-hint>
              <info-hint v-else-if="selectedStatus === 'available' && allow_late_booking" variant="primary">
                This text will be visible to customers when a slot is blocked off due to maximum time after start coming into effect.
              </info-hint>
              <info-hint v-else-if="selectedStatus === 'call_to_book'" variant="primary">
                This text will be visible to customers when a slot has the status "Call to book".
              </info-hint>
              <label class="text-white" v-if="selectedStatus === 'available'">
                {{ allow_late_booking ? 'Maximum time after start block off text' : 'Minimum notice block off text' }}
              </label>
              <label class="text-white" v-if="selectedStatus === 'call_to_book'">Time slot text</label>
              <base-input v-model="slot.slot_text"></base-input>

              <div class="toggle-row">
                <el-switch active-color="#e14eca" v-model="slot.has_external_link" />
                <label class="mb-0 ml-2" style="cursor: pointer" @click="slot.has_external_link = !slot.has_external_link">
                  Link to external page
                </label>
              </div>
              <div v-if="slot.has_external_link" class="mt-2">
                <base-input v-model="slot.external_link" placeholder="https://example.com"></base-input>
              </div>
        </drawer-section>
      </transition>

      <!-- Section 3b: Notes (non-available statuses) -->
      <transition name="section-fade">
        <drawer-section
          v-if="selectedStatus !== 'available'"
          key="block-notes"
          title="Notes"
          icon="icon-notes"
        >
          <info-hint variant="primary">
            This note will be visible to staff in the calendar to explain why the slot is blocked.
          </info-hint>
          <label class="text-white">Block note</label>
          <base-input>
            <el-input v-model="slot.block_note" :rows="3" type="textarea" placeholder="Notes..." />
          </base-input>
        </drawer-section>
      </transition>

      <!-- Custom message — staff annotation shown to customers on the booking site -->
      <drawer-section title="Custom message" icon="icon-chat-33">
        <info-hint variant="primary">
          This text shows in addition to any other text on the customer-facing slot on the booking site.
        </info-hint>
        <div class="toggle-row">
          <el-switch active-color="#e14eca" v-model="slot.custom_message_active" />
          <label class="mb-0 ml-2" style="cursor: pointer" @click="slot.custom_message_active = !slot.custom_message_active">
            Show message on this booking slot
          </label>
        </div>
        <template v-if="slot.custom_message_active">
          <label class="text-white mt-2 d-block">Message</label>
          <base-input
            v-model="slot.custom_message"
            :maxlength="200"
            placeholder="e.g. Closed for Christmas!"
            :error="errs.custom_message"
          ></base-input>
          <small v-if="slot.custom_message_display === 'badge' && slot.custom_message.length > 20" style="color: rgba(255, 255, 255, 0.7); display: block; margin-top: -8px; margin-bottom: 8px;">
            Badges read best with 20 characters or fewer.
          </small>
          <label class="text-white d-block mt-2">Display style</label>
          <segmented-toggle
            v-model="slot.custom_message_display"
            :options="custom_message_display_options"
            size="sm"
          />
        </template>
        <div v-if="slot.custom_message && slot.custom_message_active" class="custom-message-preview mt-3">
          <label class="text-white d-block mb-2">Preview</label>
          <div class="preview-frame">
            <div v-if="slot.custom_message_display === 'text'" class="preview-slot">
              <div class="preview-time">{{ getPreviewTime() }}</div>
              <div class="preview-text">{{ slot.custom_message }}</div>
            </div>
            <div v-else class="preview-slot">
              <span class="preview-badge">{{ slot.custom_message }}</span>
              <div class="preview-time">{{ getPreviewTime() }}</div>
            </div>
          </div>
        </div>
      </drawer-section>

      <!-- Section 4: Slot pricing (collapsible) — read-only summary only.
           "Edit pricing" routes to the pricing sub-view (matches BookingDrawer
           drawer-views pattern). -->
      <drawer-section
        title="Slot pricing"
        icon="icon-money-coins"
        :collapsible="true"
        :expanded="showPricing"
        @update:expanded="showPricing = $event"
      >
        <div class="detail-row text-white">
          <span>Pricing</span>
          <span>{{ enable_custom_pricing ? 'Custom' : 'Default' }}</span>
        </div>
        <div class="detail-row text-white">
          <span>Pricing type</span>
          <span>{{ custom_pricing_type.name }}</span>
        </div>
        <div class="detail-row text-white">
          <span>Reservation style</span>
          <span>{{ is_public_booking.name }}</span>
        </div>
        <div class="detail-row text-white">
          <span>Cumulative</span>
          <span>{{ cumulative_category_pricing ? 'Yes' : 'No' }}</span>
        </div>
        <div class="detail-row text-white">
          <span>Deposit</span>
          <span>{{ depositDisplayLabel }}</span>
        </div>
        <div class="mt-3">
          <button class="pricing-edit-link text-primary" @click.stop="currentView = 'pricing'">
            <i class="tim-icons icon-pencil mr-1"></i> Edit pricing
          </button>
        </div>
      </drawer-section>
    </template>

    <!-- Pricing sub-view — opens its own tab matching BookingDrawer's
         EditPricingView. The whole body switches; back arrow + footer
         "Done" return to the main form. -->
    <template v-else-if="currentView === 'pricing'">
      <div class="pricing-subview">
                <!-- Enable toggle -->
                <div class="toggle-row mb-3">
                  <el-switch active-color="#e14eca" v-model="enable_custom_pricing" />
                  <label class="mb-0 ml-2" style="cursor: pointer" @click="enable_custom_pricing = !enable_custom_pricing">
                    Enable custom pricing
                  </label>
                </div>
                <info-hint v-if="!enable_custom_pricing" variant="primary">
                  This slot will use the default pricing from the selected event.
                </info-hint>

                <template v-if="enable_custom_pricing">
                  <!-- Pricing model -->
                  <drawer-section title="Pricing model" icon="icon-coins">
                    <info-hint variant="primary">
                      Private bookings reserve the entire slot for one group. Public bookings allow multiple groups.
                    </info-hint>
                    <div class="form-row">
                      <div class="form-col">
                        <base-input label="Pricing type">
                          <el-select v-model="custom_pricing_type" class="select-primary" value-key="id" placeholder="Select pricing type">
                            <el-option v-for="(pt, i) in pricing_types" class="select-primary" :label="pt.name" :value="{ id: pt.id, name: pt.name }" :key="'PT' + i" />
                          </el-select>
                        </base-input>
                      </div>
                      <div class="form-col">
                        <base-input label="Reservation style">
                          <el-select v-model="is_public_booking" class="select-primary" value-key="id" placeholder="Reservation style">
                            <el-option v-for="(rs, i) in reservation_styles" class="select-primary" :label="rs.name" :value="{ id: rs.id, name: rs.name }" :key="'RS' + i" />
                          </el-select>
                        </base-input>
                      </div>
                    </div>

                    <!-- Pricing options -->
                    <div class="toggle-row mb-2">
                      <el-switch active-color="#e14eca" v-model="cumulative_category_pricing" />
                      <label class="mb-0 ml-2" style="cursor: pointer" @click="cumulative_category_pricing = !cumulative_category_pricing">
                        Category pricing is cumulative
                      </label>
                    </div>
                    <div class="toggle-row mb-2">
                      <el-switch active-color="#e14eca" v-model="hide_pricing_rate" />
                      <label class="mb-0 ml-2" style="cursor: pointer" @click="hide_pricing_rate = !hide_pricing_rate">
                        Hide pricing rate on booking site
                      </label>
                    </div>
                    <div class="toggle-row mb-3">
                      <el-switch active-color="#e14eca" v-model="limit_purchase_category" />
                      <label class="mb-0 ml-2" style="cursor: pointer" @click="limit_purchase_category = !limit_purchase_category">
                        Limit to one category per purchase
                        <el-popover popper-class="otc-pop" trigger="hover" placement="top">
                          <div><div class="popover-body">When enabled, customers can only purchase one pricing category at a time. When disabled, customers can combine multiple categories in a single purchase.</div></div>
                          <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                        </el-popover>
                      </label>
                    </div>
                  </drawer-section>

                  <!-- Pricing categories -->
                  <drawer-section title="Pricing categories" icon="icon-tag">
                    <div class="toggle-row mb-3">
                      <el-switch active-color="#e14eca" :value="enable_multiple_durations" @change="onDurationsToggle($event)" />
                      <label class="mb-0 ml-2" style="cursor: pointer" @click="onDurationsToggle(!enable_multiple_durations)">
                        Enable multiple durations
                        <el-popover popper-class="otc-pop" trigger="hover" placement="top">
                          <div><div class="popover-body">Offer this slot at several lengths. Each duration has its own pricing categories and tiers. Customers pick the duration after choosing a time.</div></div>
                          <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                        </el-popover>
                      </label>
                    </div>
                    <pricing-categories-editor :categories="activeCategories" :errors="pricingErrors" :set-key="activeDurationKey">
                    <duration-pills v-if="enable_multiple_durations" class="mb-3"
                                    :durations="durations" :value="active_duration_minutes"
                                    @input="selectDuration" @add="addDuration" @remove="removeDuration" @set-default="setDefaultDuration" />
                    </pricing-categories-editor>
                  </drawer-section>

                  <!-- Deposits -->
                  <drawer-section title="Deposits" icon="icon-money-coins">
                    <div class="toggle-row mb-2">
                      <el-switch active-color="#e14eca" v-model="allow_deposit" />
                      <label class="mb-0 ml-2" style="cursor: pointer" @click="allow_deposit = !allow_deposit">Allow deposit</label>
                    </div>
                    <div v-if="allow_deposit">
                      <div class="toggle-row mb-2">
                        <el-switch active-color="#e14eca" v-model="require_deposit" />
                        <label class="mb-0 ml-2" style="cursor: pointer" @click="require_deposit = !require_deposit">Require only deposit amount during checkout</label>
                      </div>
                      <div class="form-row">
                        <div class="form-col">
                          <base-input label="Deposit type">
                            <el-select v-model="deposit_type" class="select-primary" value-key="id" placeholder="Value type">
                              <el-option v-for="(type, i) in valueTypes" class="select-primary" :label="type.name" :value="{ id: type.id, name: type.name }" :key="'VT' + i" />
                            </el-select>
                          </base-input>
                        </div>
                        <div class="form-col">
                          <label class="text-white">Deposit amount</label>
                          <div class="amount-input-group">
                            <span class="amount-prefix">{{ resolvedCurrencySymbol }}</span>
                            <base-input class="amount-input-wrapper mb-0">
                              <el-input-number :controls="false" v-model="deposit_amount" :precision="2" :min="0" placeholder="0.00" />
                            </base-input>
                          </div>
                        </div>
                      </div>
                    </div>
                  </drawer-section>
                </template>
      </div>
    </template>
    </div>

    <template #footer>
      <div class="d-flex justify-content-end" style="gap: 8px;">
        <template v-if="currentView === 'pricing'">
          <base-button type="primary" size="sm" @click="exitPricingView">Done</base-button>
        </template>
        <template v-else>
          <base-button type="secondary" size="sm" @click="onClose">Cancel</base-button>
          <base-button type="primary" size="sm" :disabled="saving" @click="createSlotPrompt">
            <i v-if="saving" class="el-icon-loading mr-1"></i>Create time slot
          </base-button>
        </template>
      </div>
    </template>
  </base-drawer>
</template>

<script>
import moment from 'moment';
import { API_LOCATION } from '@/api-config';
import axios from 'axios';
import { splitCategoriesByDuration, flattenCategoriesByDuration } from '@/util/pricing';
import { Auth } from 'aws-amplify';
import swal from 'sweetalert2';
import currencies from '@/assets/currencies.json';
import {
  pickerDateFormat as resolvePickerDateFormat,
  pickerTimeFormat as resolvePickerTimeFormat
} from '@/plugins/dates';
import {
  TimePicker,
  DatePicker,
  Select,
  Option,
  Switch,
  Input,
  InputNumber
} from 'element-ui';

export default {
  name: 'CreateSlotDrawer',

  components: {
    [TimePicker.name]: TimePicker,
    [DatePicker.name]: DatePicker,
    [Select.name]: Select,
    [Option.name]: Option,
    [Switch.name]: Switch,
    [Input.name]: Input,
    [InputNumber.name]: InputNumber
  },

  props: {
    show: { type: Boolean, default: false },
    /** Pre-selected game object (optional — user can still change it) */
    game: { type: Object, default: null },
    /** Pre-selected game ID (used if game prop is not provided) */
    gameId: { type: [Number, String], default: null },
    /** Pre-populated date (YYYY-MM-DD string or Date) */
    initialDate: { type: [String, Date], default: null },
    /** Pre-populated start time (HH:mm:ss string or Date) */
    initialStartTime: { type: [String, Date], default: null },
    /** Pre-populated end time (HH:mm:ss string or Date) */
    initialEndTime: { type: [String, Date], default: null },
    companyId: { type: [String, Number], required: true },
    companyGroupId: { type: [String, Number], required: true },
    dateFormat: { type: String, default: 'MM-DD-YYYY' },
    timeFormat: { type: String, default: 'hh:mm A' },
    firstDayOfWeek: { type: Number, default: 0 },
    showDayOfWeek: { type: Boolean, default: false },
    customStatuses: { type: Array, default: () => [] },
    currencySymbol: { type: String, default: '$' },
    currency: { type: String, default: 'USD' }
  },

  data() {
    return {
      ready: false,
      saving: false,
      games: [],
      selectedGame: null,
      selectedStatus: 'available',
      statuses: [
        { label: 'Available', value: 'available' },
        { label: 'Blocked', value: 'blocked' },
        { label: 'Call to book', value: 'call_to_book' },
        { label: 'Requestable', value: 'requestable' }
      ],
      slot: {
        start_date: null,
        start_time: null,
        end_date: null,
        end_time: null,
        block_note: '',
        slot_text: '',
        is_all_day: false,
        all_day_text: '',
        has_external_link: false,
        external_link: '',
        custom_message: '',
        custom_message_display: 'text',
        custom_message_active: false
      },
      custom_message_display_options: [
        { value: 'text', label: 'Text' },
        { value: 'badge', label: 'Badge' }
      ],
      // Booking notice defaults (matching Calendar.vue period IDs)
      allow_late_booking: false,
      hide_past_min_notice: 0,
      request_past_min_notice: 0,
      // Booking period values store plain ids (matches ValuePeriodInput's
      // contract). Submit code reads these directly — no `.id` access.
      min_booking_value: 1,
      min_booking_period: 1,
      max_booking_value: 6,
      max_booking_period: 2,
      min_periods: [
        { id: 0, label: 'Minutes' },
        { id: 1, label: 'Hours' },
        { id: 2, label: 'Days' },
        { id: 3, label: 'Weeks' },
        { id: 4, label: 'Months' }
      ],
      max_periods: [
        { id: 0, label: 'Days' },
        { id: 1, label: 'Weeks' },
        { id: 2, label: 'Months' },
        { id: 3, label: 'Years' }
      ],
      // View routing — 'main' shows the create-slot form, 'pricing' shows
      // the pricing editor as its own sub-view (matches BookingDrawer's
      // drawer-views pattern).
      currentView: 'main',
      // Pricing state
      showPricing: true,
      enable_custom_pricing: false,
      custom_pricing_type: { id: 1, name: 'Per person' },
      pricing_types: [
        { id: 1, name: 'Per person' },
        { id: 2, name: 'Flat rate' }
      ],
      is_public_booking: { id: 0, name: 'Private' },
      reservation_styles: [
        { id: 0, name: 'Private' },
        { id: 1, name: 'Public' }
      ],
      cumulative_category_pricing: false,
      hide_pricing_rate: false,
      limit_purchase_category: false,
      custom_pricing_categories: [
        { category: 'General', pricing: [{ min_players: 1, max_players: 10, price: 0 }], minimum_participants: 0, maximum_participants: undefined, default_participants: 1 }
      ],
      allow_deposit: false,
      require_deposit: false,
      deposit_type: { id: 0, name: 'Per booking' },
      deposit_amount: 0,
      valueTypes: [
        { id: 0, name: 'Per booking' },
        { id: 1, name: 'Per participant' }
      ],
      errs: { game: '', start_date: '', start_time: '', end_date: '', end_time: '', custom_message: '' },
      pricingErrors: {},
      pricingErrorsBySet: {},
      enable_multiple_durations: false,
      durations: [],
      active_duration_minutes: null
    };
  },

  computed: {
    activeDurationSet() {
      if (this.active_duration_minutes === null || this.active_duration_minutes === undefined) return null;
      return (this.durations || []).find(d => Number(d.duration_minutes) === Number(this.active_duration_minutes) && !d.is_default) || null;
    },
    activeCategories() {
      return this.activeDurationSet ? this.activeDurationSet.pricing_categories : this.custom_pricing_categories;
    },
    activeDurationKey() {
      return this.activeDurationSet ? String(this.activeDurationSet.duration_minutes) : 'default';
    },
    // Single control over the two past-notice columns. 0 = call to book
    // (both flags off), 1 = request to book, 2 = hide slot.
    pastNoticeBehavior: {
      get() {
        if (Number(this.request_past_min_notice) === 1) return 1;
        if (Number(this.hide_past_min_notice) === 1) return 2;
        return 0;
      },
      set(v) {
        this.request_past_min_notice = v === 1 ? 1 : 0;
        this.hide_past_min_notice = v === 2 ? 1 : 0;
      }
    },
    // Title swaps when navigating into the pricing sub-view (mirrors
    // BookingDrawer's drawer-views, which retitle the drawer header).
    drawerTitle() {
      if (this.currentView === 'pricing') return 'Custom pricing';
      return 'Create time slot';
    },

    allStatuses() {
      const custom = this.customStatuses
        .filter(s => s.is_active)
        .map(s => ({
          label: s.display_name,
          value: s.status_key
        }));
      return [...this.statuses, ...custom];
    },

    // Picker formats sourced from group settings via the shared dates.js
    // helpers — handles every separator variant (slash, dash, dot) and both
    // time format flags ('12h' / 'HH:mm' / '24h').
    dateFormatCal() {
      return resolvePickerDateFormat(this.dateFormat);
    },

    dayOfWeek() {
      return this.showDayOfWeek ? 'ddd, ' : '';
    },

    pickerTimeFormat() {
      return resolvePickerTimeFormat(this.timeFormat);
    },

    resolvedCurrencySymbol() {
      const info = currencies.currencies.find(c => c.code === this.currency);
      return info ? info.symbol : this.currencySymbol;
    },

    depositDisplayLabel() {
      if (!this.allow_deposit) return 'Disabled';
      const amount = this.deposit_amount || 0;
      const prefix = this.require_deposit ? 'Required' : 'Allowed';
      const formatted = this.resolvedCurrencySymbol + amount;
      return `${prefix}, ${formatted}`;
    }
  },

  watch: {
    show(newVal) {
      if (newVal) {
        this._previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', this._onEscKey);
        this.initializeForm();
      } else {
        document.body.style.overflow = this._previousOverflow || '';
        document.removeEventListener('keydown', this._onEscKey);
      }
    },
    selectedGame(newGame) {
      // Re-seed pricing defaults from the newly selected event, but only
      // when the user is on default pricing — don't clobber in-progress
      // custom edits.
      if (!this.enable_custom_pricing) {
        this.seedPricingFromGame(newGame);
      }
    },
    enable_custom_pricing(isCustom) {
      // Turning custom pricing off should snap the summary back to the
      // game's defaults, not leave it showing the user's prior edits.
      if (!isCustom) {
        this.seedPricingFromGame(this.selectedGame);
      }
    }
  },

  created() {
    this._onEscKey = (e) => {
      if (e.key === 'Escape' && this.show) {
        this.onClose();
      }
    };
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this._onEscKey);
    document.body.style.overflow = this._previousOverflow || '';
  },

  methods: {
    async getAuthToken() {
      const session = await Auth.currentSession();
      return session.getIdToken().getJwtToken();
    },

    getPreviewTime() {
      if (this.slot.is_all_day) return this.slot.all_day_text || 'All day';
      if (!this.slot.start_time) return '12:00 PM';
      const time = moment(this.slot.start_time);
      return time.isValid() ? time.format('h:mm A') : '12:00 PM';
    },

    async initializeForm() {
      this.ready = false;
      this.saving = false;
      this.selectedStatus = 'available';
      this.showPricing = true;
      this.currentView = 'main';
      this.slot = {
        start_date: null,
        start_time: null,
        end_date: null,
        end_time: null,
        block_note: '',
        slot_text: '',
        is_all_day: false,
        all_day_text: '',
        has_external_link: false,
        external_link: ''
      };
      this.allow_late_booking = false;
      this.hide_past_min_notice = 0;
      this.request_past_min_notice = 0;
      this.min_booking_value = 1;
      this.min_booking_period = 1;
      this.max_booking_value = 6;
      this.max_booking_period = 2;
      // Reset pricing
      this.enable_custom_pricing = false;
      this.custom_pricing_type = { id: 1, name: 'Per person' };
      this.is_public_booking = { id: 0, name: 'Private' };
      this.cumulative_category_pricing = false;
      this.hide_pricing_rate = false;
      this.limit_purchase_category = false;
      this.custom_pricing_categories = [
        { category: 'General', pricing: [{ min_players: 1, max_players: 10, price: 0 }], minimum_participants: 0, maximum_participants: undefined, default_participants: 1 }
      ];
      this.allow_deposit = false;
      this.require_deposit = false;
      this.deposit_type = { id: 0, name: 'Per booking' };
      this.deposit_amount = 0;
      this.errs = { game: '', start_date: '', start_time: '', end_date: '', end_time: '', custom_message: '' };
      this.pricingErrors = {};

      // Load games
      await this.loadGames();

      // Pre-select game (user can still change it)
      if (this.game) {
        this.selectedGame = this.games.find(g => g.id === this.game.id) || this.game;
      } else if (this.gameId) {
        this.selectedGame = this.games.find(g => g.id === Number(this.gameId)) || null;
      } else if (this.games.length === 1) {
        this.selectedGame = this.games[0];
      } else {
        this.selectedGame = null;
      }

      // Seed default pricing fields from the selected event so the slot
      // pricing summary mirrors what the slot will actually inherit.
      this.seedPricingFromGame(this.selectedGame);

      // Pre-populate date
      if (this.initialDate) {
        const d = typeof this.initialDate === 'string'
          ? moment(this.initialDate.substring(0, 10), 'YYYY-MM-DD').toDate()
          : new Date(this.initialDate);
        this.slot.start_date = d;
        this.slot.end_date = new Date(d);
      } else {
        this.slot.start_date = new Date();
        this.slot.end_date = new Date();
      }

      // Pre-populate times. Parent is the source of truth — if it dragged a
      // 30-minute range we respect that. Only fall back to a 1-hour default
      // when no end time was supplied at all.
      if (this.initialStartTime) {
        this.slot.start_time = this.parseTimeToDate(this.initialStartTime);
      }
      if (this.initialEndTime) {
        this.slot.end_time = this.parseTimeToDate(this.initialEndTime);
      } else if (this.slot.start_time) {
        this.slot.end_time = new Date(this.slot.start_time.getTime() + 60 * 60 * 1000);
      }

      this.ready = true;
    },

    parseTimeToDate(val) {
      if (val instanceof Date) return val;
      const parts = val.split(':');
      const d = new Date();
      d.setHours(parseInt(parts[0]), parseInt(parts[1]), parseInt(parts[2] || 0), 0);
      return d;
    },

    async loadGames() {
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/games`;
        const response = await axios.get(url, config);
        this.games = (response.data || []).filter(g => g.is_enabled !== 0);
      } catch (error) {
        console.error('Failed to load games:', error);
      }
    },

    seedPricingFromGame(g) {
      if (!g) return;
      this.custom_pricing_type =
        this.pricing_types.find(pt => pt.id === g.pricing_type_id) || { id: 1, name: 'Per person' };
      this.is_public_booking =
        this.reservation_styles.find(r => r.id === g.is_public_booking) || { id: 0, name: 'Private' };
      this.cumulative_category_pricing = Boolean(g.cumulative_category_pricing);
      this.hide_pricing_rate = Boolean(g.hide_pricing_rate);
      this.limit_purchase_category = Boolean(g.limit_purchase_category);
      this.allow_deposit = Boolean(g.allow_deposit);
      this.require_deposit = Boolean(g.require_deposit);
      this.deposit_type =
        this.valueTypes.find(v => v.id === g.deposit_type) || { id: 0, name: 'Per booking' };
      this.deposit_amount = g.deposit_amount ?? 0;

      const gamePricing = typeof g.game_pricing === 'string'
        ? JSON.parse(g.game_pricing)
        : g.game_pricing;
      if (gamePricing && gamePricing.length > 0) {
        this.custom_pricing_categories = this.getCategories(gamePricing);
      } else {
        this.custom_pricing_categories = [
          { category: 'General', pricing: [{ min_players: 1, max_players: 10, price: 0 }], minimum_participants: 0, maximum_participants: undefined, default_participants: 1 }
        ];
      }
      //Multiple durations: seed the event's duration list; each extra duration starts from the default set
      let gameDurations = [];
      try { gameDurations = typeof g.game_durations === 'string' ? JSON.parse(g.game_durations) : (g.game_durations || []); } catch (e) { gameDurations = []; }
      this.enable_multiple_durations = Boolean(g.enable_multiple_durations) && gameDurations.length > 0;
      this.durations = gameDurations.map(d => ({
        duration_minutes: Number(d.duration_minutes),
        is_default: Number(d.is_default) === 1,
        position: Number(d.position) || 0,
        pricing_categories: Number(d.is_default) === 1 ? [] : JSON.parse(JSON.stringify(this.custom_pricing_categories))
      }));
      this.active_duration_minutes = null;
    },

    getCategories(items) {
      if (!items || items.length === 0) return [];
      const groupedArray = [];
      items.forEach(item => {
        const { category_id, category, minimum_participants, maximum_participants, default_participants, min_players, max_players, price } = item;
        let group = groupedArray.find(g => g.category_id === category_id);
        if (!group) {
          group = { category_id, category, maximum_participants, minimum_participants, default_participants, game_duration_id: item.game_duration_id === undefined ? null : item.game_duration_id, pricing: [] };
          groupedArray.push(group);
        }
        group.pricing.push({ min_players, max_players, price });
      });
      groupedArray.sort((a, b) => a.category_id - b.category_id);
      return groupedArray;
    },

    handleAllDayToggle() {
      if (this.slot.is_all_day) {
        const startDate = new Date(this.slot.start_date || new Date());
        startDate.setHours(0, 0, 0, 0);
        this.slot.start_time = startDate;
        const endDate = new Date(this.slot.start_date || new Date());
        endDate.setHours(23, 59, 0, 0);
        this.slot.end_time = endDate;
        this.slot.end_date = new Date(this.slot.start_date || new Date());
        this.errs.start_time = '';
        this.errs.end_time = '';
      }
    },

    // Pricing helpers

    //Multiple durations: the default duration's categories stay in custom_pricing_categories;
    //every other duration owns its own array under durations[n].pricing_categories.
    selectDuration(minutes) {
      this.active_duration_minutes = minutes;
      this.pricingErrors = this.pricingErrorsBySet[this.activeDurationKey] || {};
    },
    ensureDefaultDuration() {
      if (!(this.durations || []).some(d => d.is_default)) {
        this.durations.unshift({ duration_minutes: 60, is_default: true, position: 0, pricing_categories: [] });
      }
    },
    onDurationsToggle(on) {
      this.enable_multiple_durations = !!on;
      if (on) this.ensureDefaultDuration();
      this.selectDuration(null);
      this.syncEndToDefaultDuration();
    },
    addDuration(minutes) {
      this.ensureDefaultDuration();
      this.durations.push({
        duration_minutes: minutes,
        is_default: false,
        position: this.durations.length,
        pricing_categories: JSON.parse(JSON.stringify(this.custom_pricing_categories))
      });
      this.selectDuration(minutes);
    },
    removeDuration(minutes) {
      const idx = this.durations.findIndex(d => Number(d.duration_minutes) === Number(minutes) && !d.is_default);
      if (idx < 0) return;
      this.durations.splice(idx, 1);
      if (Number(this.active_duration_minutes) === Number(minutes)) this.selectDuration(null);
    },
    setDefaultDuration(minutes) {
      const next = this.durations.find(d => Number(d.duration_minutes) === Number(minutes) && !d.is_default);
      const current = this.durations.find(d => d.is_default);
      if (!next) return;
      const nextCategories = next.pricing_categories;
      next.pricing_categories = [];
      next.is_default = true;
      if (current) {
        current.is_default = false;
        current.pricing_categories = this.custom_pricing_categories;
      }
      this.custom_pricing_categories = nextCategories;
      this.pricingErrorsBySet = {};
      this.selectDuration(null);
      this.syncEndToDefaultDuration();
    },
    //The slot's end follows the default duration when the feature is on (convenience only)
    syncEndToDefaultDuration() {
      if (!this.enable_custom_pricing || !this.enable_multiple_durations || !this.slot.start_time || this.slot.is_all_day) return;
      const def = (this.durations || []).find(d => d.is_default);
      if (!def) return;
      this.slot.end_time = new Date(new Date(this.slot.start_time).getTime() + Number(def.duration_minutes) * 60 * 1000);
    },





    validateForm() {
      let isValid = true;
      this.errs = { game: '', start_date: '', start_time: '', end_date: '', end_time: '', custom_message: '' };

      if (this.slot.custom_message_active && (!this.slot.custom_message || !this.slot.custom_message.trim())) {
        this.errs.custom_message = 'Add a message before enabling, or turn the toggle off';
        isValid = false;
      }

      if (!this.selectedGame) {
        this.errs.game = 'Please select a game';
        isValid = false;
      }

      if (!this.slot.start_date) {
        this.errs.start_date = 'Start date is required';
        isValid = false;
      }

      if (!this.slot.end_date && !this.slot.is_all_day) {
        this.errs.end_date = 'End date is required';
        isValid = false;
      }

      // Date order
      if (this.slot.start_date && this.slot.end_date) {
        const start = moment(this.slot.start_date).startOf('day');
        const end = moment(this.slot.end_date).startOf('day');
        if (end.isBefore(start)) {
          this.errs.end_date = 'End date cannot be before start date';
          isValid = false;
        }
      }

      if (!this.slot.is_all_day) {
        if (!this.slot.start_time) {
          this.errs.start_time = 'Start time is required';
          isValid = false;
        }
        if (!this.slot.end_time) {
          this.errs.end_time = 'End time is required';
          isValid = false;
        }

        // Time order (same day)
        if (this.slot.start_time && this.slot.end_time && this.slot.start_date && this.slot.end_date) {
          const startDate = moment(this.slot.start_date).startOf('day');
          const endDate = moment(this.slot.end_date).startOf('day');
          if (startDate.isSame(endDate)) {
            const startTime = moment(moment(this.slot.start_time).format('HH:mm:ss'), 'HH:mm:ss');
            const endTime = moment(moment(this.slot.end_time).format('HH:mm:ss'), 'HH:mm:ss');
            if (startTime.isSameOrAfter(endTime)) {
              this.errs.end_time = 'End time must be after start time';
              isValid = false;
            }
          }
        }
      }

      if (this.enable_custom_pricing && !this.validatePricing()) {
        isValid = false;
      }

      return isValid;
    },

    validateCategorySet(categories) {
      const errors = {};
      for (let i = 0; i < categories.length; i++) {
        const cat = categories[i];
        const catErrs = {};

        if (!cat.category || !String(cat.category).trim()) {
          catErrs.category = 'Category cannot be empty';
        }

        const min = cat.minimum_participants;
        const max = cat.maximum_participants;
        const def = cat.default_participants;

        if (Number.isFinite(max) && Number.isFinite(min) && max < min) {
          catErrs.maximum_participants = 'Maximum participants cannot be less than minimum participants';
        } else if (Number.isFinite(max) && max <= 0) {
          catErrs.maximum_participants = 'Maximum participants cannot be 0 or less';
        }

        if (!Number.isFinite(min)) {
          catErrs.minimum_participants = 'Minimum participants cannot be empty';
        }

        if (!Number.isFinite(def)) {
          catErrs.default_participants = 'Default participants cannot be empty';
        } else if (Number.isFinite(min) && def < min) {
          catErrs.default_participants = 'Default participants cannot be less than minimum participants';
        } else if (Number.isFinite(max) && def > max) {
          catErrs.default_participants = 'Default participants cannot be greater than maximum participants';
        }

        const tierErrs = {};
        for (let j = 0; j < cat.pricing.length; j++) {
          const tier = cat.pricing[j];
          const te = {};

          if (!Number.isFinite(tier.price)) {
            te.price = 'Price cannot be empty';
          }

          if (!Number.isFinite(tier.max_players)) {
            te.max_players = 'Invalid value';
          } else if (Number.isFinite(tier.min_players) && tier.max_players < tier.min_players) {
            te.max_players = 'Invalid range';
            te.min_players = 'Invalid range';
          }

          if (!Number.isFinite(tier.min_players)) {
            te.min_players = 'Invalid value';
          } else if (Number.isFinite(tier.max_players) && tier.max_players < tier.min_players) {
            te.max_players = 'Invalid range';
            te.min_players = 'Invalid range';
          }

          if (Object.keys(te).length > 0) tierErrs[j] = te;
        }
        if (Object.keys(tierErrs).length > 0) catErrs.tiers = tierErrs;

        if (Object.keys(catErrs).length > 0) {
          errors[i] = catErrs;
        }
      }
      return errors;
    },

    validatePricing() {
      this.pricingErrorsBySet = {};
      this.pricingErrors = {};
      if (!this.enable_custom_pricing) return true;
      let isValid = true;
      let firstFailing;
      const sets = [{ key: 'default', categories: this.custom_pricing_categories, minutes: null }];
      if (this.enable_multiple_durations) {
        for (const d of (this.durations || [])) {
          if (!d.is_default) sets.push({ key: String(d.duration_minutes), categories: d.pricing_categories || [], minutes: Number(d.duration_minutes) });
        }
      }
      for (const set of sets) {
        const errs = this.validateCategorySet(set.categories);
        if (Object.keys(errs).length > 0 || (set.minutes !== null && set.categories.length === 0)) {
          isValid = false;
          if (firstFailing === undefined) firstFailing = set.minutes;
        }
        this.pricingErrorsBySet[set.key] = errs;
      }
      if (!isValid && firstFailing !== undefined) this.active_duration_minutes = firstFailing;
      this.pricingErrors = this.pricingErrorsBySet[this.activeDurationKey] || {};

      if (!isValid) {
        this.$notify({
          type: 'danger',
          message: 'Fix the highlighted pricing errors before saving',
          icon: 'tim-icons icon-alert-circle-exc',
          timeout: 4000
        });
      }

      return isValid;
    },

    exitPricingView() {
      if (!this.validatePricing()) return;
      this.currentView = 'main';
    },

    async validateResources() {
      const start = moment(this.slot.start_date).format('YYYY-MM-DD');
      const end = moment(this.slot.end_date).format('YYYY-MM-DD');
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/staff/validate?start=${start}&end=${end}`;
        const response = await axios.get(url, config);
        return response.data;
      } catch (err) {
        console.error(err);
        if (err.response) {
          if (err.response.status === 401) {
            this.$router.push('/forbidden');
            return 'Unauthorized';
          }
          if (err.response.status === 500) {
            return err.response.data || 'Insufficient resources available';
          }
          return 'An error occurred during validation';
        }
        return 'Network error - please try again';
      }
    },

    async createSlotPrompt() {
      // Re-entry guard — the button is disabled while saving, but the
      // resource-validation request and the confirm dialog both run
      // before saving flips true. Flip it up front so a second click
      // during that window can't trigger a duplicate create.
      if (this.saving) return;
      this.saving = true;

      if (!this.validateForm()) {
        this.saving = false;
        return;
      }

      const validator = await this.validateResources();
      if (validator !== 'Success') {
        const result = await swal.fire({
          title: 'Are you sure?',
          text: validator,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'I am sure',
          cancelButtonText: 'Cancel',
          confirmButtonClass: 'btn btn-primary',
          cancelButtonClass: 'btn btn-secondary',
          buttonsStyling: false
        });
        if (!result.isConfirmed) {
          this.saving = false;
          return;
        }
      }

      await this.createTimeSlot();
    },

    async createTimeSlot() {
      this.saving = true;
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/bookings`;

        // Find status_type_id if this is a custom status
        const customStatus = this.customStatuses.find(s => s.status_key === this.selectedStatus);

        const data = {
          game_id: this.selectedGame.id,
          game_name: this.selectedGame.name,
          booking_date: moment(this.slot.start_date).format('YYYY-MM-DD'),
          booking_end_date: moment(this.slot.end_date).format('YYYY-MM-DD'),
          status: this.selectedStatus,
          status_type_id: customStatus?.id || null,
          start_time: this.slot.is_all_day ? '00:00:00' : moment(this.slot.start_time).format('HH:mm:ss'),
          end_time: this.slot.is_all_day ? '23:59:59' : moment(this.slot.end_time).format('HH:mm:ss'),
          is_all_day: this.slot.is_all_day,
          all_day_text: this.slot.all_day_text || '',
          slot_text: this.slot.slot_text || '',
          block_note: this.slot.block_note || '',
          has_external_link: this.slot.has_external_link,
          external_link: this.slot.external_link || '',
          custom_message: this.slot.custom_message || '',
          custom_message_display: this.slot.custom_message_display || 'text',
          custom_message_active: this.slot.custom_message_active ? 1 : 0,
          min_booking_value: this.min_booking_value,
          min_booking_period: this.min_booking_period,
          allow_late_booking: this.allow_late_booking,
          hide_past_min_notice: this.hide_past_min_notice,
          request_past_min_notice: this.request_past_min_notice,
          max_booking_period: this.max_booking_period,
          max_booking_value: this.max_booking_value,
          is_custom_pricing_enabled: this.enable_custom_pricing
        };

        // Include custom pricing fields when enabled
        if (this.enable_custom_pricing) {
          data.pricing_type_id = this.custom_pricing_type.id;
          data.allow_deposit = this.allow_deposit;
          data.cumulative_category_pricing = this.cumulative_category_pricing;
          data.hide_pricing_rate = this.hide_pricing_rate;
          data.limit_purchase_category = this.limit_purchase_category;
          data.deposit_amount = this.deposit_amount;
          data.require_deposit = this.require_deposit;
          data.deposit_type = this.deposit_type.id;
          data.custom_pricing = flattenCategoriesByDuration(this.custom_pricing_categories, this.enable_multiple_durations ? this.durations : []).map(cat => ({
            ...cat,
            maximum_participants: cat.maximum_participants ?? null
          }));
          data.enable_multiple_durations = this.enable_multiple_durations ? 1 : 0;
          data.durations = this.enable_multiple_durations ? this.durations.map(d => ({ duration_minutes: Number(d.duration_minutes), is_default: d.is_default ? 1 : 0, position: Number(d.position) || 0 })) : [];
          data.is_public_booking = this.is_public_booking.id;
        }

        const response = await axios.post(url, data, config);

        this.$notify({
          type: 'success',
          message: 'Time slot created',
          icon: 'tim-icons icon-check-2',
          timeout: 3000
        });

        this.$emit('created', response.data);
      } catch (err) {
        console.error(err);
        if (err.response?.status === 401) {
          this.$router.push('/forbidden');
        } else if (err.response?.status === 500) {
          swal('Error', err.response.data, 'error');
        } else {
          swal('Error', 'Something went wrong', 'error');
        }
      } finally {
        this.saving = false;
      }
    },

    onClose() {
      this.$emit('update:show', false);
      this.$emit('close');
    }
  }
};
</script>

<style lang="scss" scoped>
// Back arrow shown in BaseDrawer's title-prefix slot when in a sub-view —
// mirrors the back-btn used by BookingDrawer's drawer-views/* headers.
.csd-back-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.7);
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
  }
}

// ============================================
// Section fade transition
// ============================================
.section-fade-enter-active,
.section-fade-leave-active {
  transition: opacity 0.15s ease;
}
.section-fade-enter,
.section-fade-leave-to {
  opacity: 0;
}

// ============================================
// Form elements (matches EditSlotView exactly)
// ============================================
.section-sublabel {
  text-transform: uppercase;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
}

.form-row {
  display: flex;
  gap: 12px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0;
  }
}

.form-col {
  flex: 1;
}

.toggle-row {
  display: flex;
  align-items: center;

  label {
    color: #fff;
    font-size: 13px;
  }
}

.settings-group {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  padding: 16px;
}

.detail-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 13px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);

  &:last-of-type {
    border-bottom: none;
  }
}

.pricing-edit-link {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 13px;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
}

// ============================================
// Pricing edit mode (matches EditPricingView)
// ============================================
.pricing-edit-section {
  // wrapper only
}












.amount-input-group {
  display: flex;
  align-items: stretch;
  border: 1px solid rgba(225, 78, 202, 0.4);
  border-radius: 0.4285rem;
  overflow: hidden;
  background: rgba(30, 30, 47, 0.6);
  height: 32px;
  &:focus-within { border-color: #e14eca; }
}

.amount-prefix {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  background: rgba(225, 78, 202, 0.15);
  font-weight: 600;
  font-size: 12px;
  min-width: 28px;
  color: #e14eca;
}

.amount-input-wrapper {
  flex: 1;
  ::v-deep .form-control { border: none; border-radius: 0; background: transparent; }
  ::v-deep .el-input-number { width: 100%; }
  ::v-deep .el-input-number .el-input__inner { text-align: left; color: white; background: transparent; border: none; }
}



// ============================================
// Custom message preview (mirrors EditSlotView)
// ============================================
.custom-message-preview {
  background: rgba(30, 30, 47, 0.6);
  border: 1px dashed rgba(255, 255, 255, 0.2);
  border-radius: 0.4285rem;
  padding: 12px;
}
.preview-frame {
  display: flex;
  justify-content: center;
  padding: 8px 0;
}
.preview-slot {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border: 1px solid #e14eca;
  border-radius: 0.4285rem;
  color: #e14eca;
  background: transparent;
  min-width: 140px;
  text-align: center;
}
.preview-time {
  font-size: 14px;
  font-weight: 600;
}
.preview-text {
  font-size: 11px;
  margin-top: 4px;
  word-break: normal;
  overflow-wrap: break-word;
}
.preview-badge {
  position: absolute;
  top: -10px;
  right: -8px;
  background: #e14eca;
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 10px;
  white-space: nowrap;
  max-width: 90%;
  overflow: hidden;
  text-overflow: ellipsis;
}

// ============================================
// Skeleton loader
// ============================================
.skeleton-section { margin-bottom: 16px; }

.skeleton-row {
  background: rgba(255, 255, 255, 0.06);
  border-radius: 4px;
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}

.skeleton-form-row {
  display: flex;
  gap: 12px;
}

@keyframes skeleton-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

// Slide transition + responsive width are now handled by BaseDrawer.
</style>

<style lang="scss">
// Input number overrides for pricing (matches EditPricingView)
.create-slot-form {
  .el-input-number--small {
    width: 100%;
  }


  .amount-input-group .el-input-number .el-input__inner {
    text-align: left;
    color: white;
    background: transparent;
    border: none;
    height: 30px;
    line-height: 30px;
    font-size: 13px;
  }
}

// Light mode overrides for inner content (BaseDrawer + DrawerSection
// handle their own light-mode styling).
.white-content .csd-back-btn {
  background: #f0f2f5;
  border-color: #d1d5db;
  color: #525f7f;

  &:hover {
    background: #e2e6ea;
    color: #344675;
  }
}

.white-content .create-slot-form {
  .section-sublabel { color: #525f7f; }
  .toggle-row label { color: #525f7f; }
  .settings-group { background: #f0f2f5; border-color: #d1d5db; }
  .detail-row { border-bottom-color: rgba(0, 0, 0, 0.05); }
  .amount-input-group { background: #ffffff; border-color: rgba(225, 78, 202, 0.3); }

  label.text-white { color: #525f7f !important; }
  .text-white { color: #344675 !important; }

  .amount-input-group .el-input-number .el-input__inner { color: #344675; }

  /* Custom message preview — dark frame washes out on white card; soft on white instead */
  .custom-message-preview {
    background: #f8f9fa;
    border-color: rgba(0, 0, 0, 0.15);
  }
  .preview-slot {
    background: #ffffff;
  }
}
</style>
